/**
 * Saying what a limit says, in words — TECHNICAL_DESIGN.md §5.3.
 *
 * A rule stores `operator` and `threshold` as the store understands them:
 * `max` and `15`, `between` and `{min, max}`, `in` and a list, and — for the
 * templates that constrain something other than a number — an operator and a
 * threshold that are both null.
 *
 * Every screen used to print those two fields raw, which produced three
 * different kinds of unreadable row on the same table:
 *
 *   * `between [object Object]` — a range threshold stringified, which is what
 *     `String({min: 3, max: 7})` gives you. The number the reviewer needed was
 *     on the screen and not on the screen at the same time.
 *   * `—` on every narrative, policy and cure rule, which reads as *missing*
 *     when it means *this rule is not a number*. A dash is the one answer that
 *     cannot be told apart from a bug.
 *   * `max 15` — correct, and still a translation the reader performs. `at most
 *     15%` is the sentence the mandate itself uses.
 *
 * So the formatting lives here once rather than in each table. The unit comes
 * from the measure's *name* deliberately: `measure_def.unit` is the authority
 * and is not on the wire, so this appends `%` only to the suffixes that can
 * mean nothing else, and appends nothing at all where it does not recognise
 * one. Guessing wrong on a compliance limit is worse than saying less, and
 * saying less is exactly what these rows did before.
 */

/** The three layers of §5.7, in the words a client would use for them. */
export const LAYERS: Record<string, string> = {
  regulatory: 'Regulation',
  mandate: 'Client mandate',
  house: 'House policy',
}

export function layerLabel(layer: string): string {
  return LAYERS[layer] ?? layer
}

/**
 * What a rule constrains, in words.
 *
 * The store keys a constraint on `scheme:selector:value`, which is the right
 * identity and the wrong caption: a portfolio-wide limit came out as
 * `None:portfolio:*`, three tokens of which two are punctuation and one is a
 * Python `None` that reached a screen.
 */
export function subjectText(subject: Record<string, unknown> | null | undefined): string {
  let said = holdingsText(subject)
  const more = (words: string) => `${said === 'the whole fund' ? 'holdings' : said} ${words}`
  // Holdings of another classification kept or set aside, in a rating band, or outside the
  // benchmark say so (decisions 61, 63 to 65).
  for (const [name, words] of [['only', 'in'], ['except', 'other than']] as const) {
    const kept = (subject?.[name] ?? {}) as Record<string, string | string[]>
    for (const [scheme, codes] of Object.entries(kept)) {
      said = more(`${words} ${Array.isArray(codes) ? codes.join(', ') : codes} (${scheme.replace(/_/g, ' ')})`)
    }
  }
  if (subject?.rated_below) said = more(`rated below ${String(subject.rated_below)}`)
  if (subject?.rated_at_least) said = more(`rated ${String(subject.rated_at_least)} or above`)
  if (subject?.benchmark === 'outside') said = more('outside the benchmark')
  return said
}

function holdingsText(subject: Record<string, unknown> | null | undefined): string {
  const selector = subject?.selector as string | undefined
  const scheme = subject?.scheme as string | undefined
  const value = subject?.value
  const everyValue = value === undefined || value === null || value === '*'
  const schemeName = scheme ? scheme.replace(/_/g, ' ') : ''
  const named = Array.isArray(value) ? value.join(', ') : String(value)
  // `corporates (issuer group)` — the holdings a classification value picks out.
  const among = scheme && !everyValue ? `${named} (${schemeName})` : ''

  // Each is judged on its own, among the holdings the classification picks out if
  // it names one. This used to read *the whole fund* for any `*`, which is what a
  // per-issuer limit on every issuer looked like, and it is not the whole fund.
  if (selector === 'issuer') return among ? `each issuer in ${among}` : 'each issuer'
  if (selector === 'security') return among ? `each security in ${among}` : 'each security'
  if (!selector || selector === 'portfolio') return among || 'the whole fund'
  // Grouped by a classification: each country, each value of it.
  if (scheme && everyValue) return `each ${schemeName}`
  return among || named
}

/** The same, from the `scheme:selector:value` string the constraint carries —
 *  for the places that have the key and not the subject. */
export function subjectKeyText(key: string): string {
  // Beyond the first three, `rated_below=BBB-` and `benchmark=outside` where named (decision 61).
  const [scheme, selector, value, ...named] = key.split(':')
  // `only` and `except` arrive as `instrument_type~cds,ndf;currency_bucket~hard`, and were read
  // as one string, which the loop over its classifications spelled out a letter at a time.
  const extra = Object.fromEntries(
    named.map((part) => {
      const [name, said = ''] = part.split('=') as [string, string?]
      if (name !== 'only' && name !== 'except') return [name, said]
      const kept = said.split(';').filter(Boolean).map((pair) => {
        const [scheme, codes = ''] = pair.split('~')
        return [scheme, codes.split(',')]
      })
      return [name, Object.fromEntries(kept)]
    }),
  )
  return subjectText({
    scheme: scheme && scheme !== 'None' ? scheme : undefined,
    selector: selector && selector !== 'None' ? selector : undefined,
    value: value === '*' || value === 'None' ? '*' : value,
    ...extra,
  })
}

/** Suffixes that fix a measure's unit beyond argument. Order matters: every
 *  percentage measure ends `_pct`, and `days_to_liquidate` puts its unit at the
 *  front. Anything unrecognised gets no unit rather than a plausible one. */
const UNITS: [RegExp, string][] = [
  [/_pct$/, '%'],
  [/_days$/, ' days'],
  [/^days_to_/, ' days'],
  [/_years$/, ' years'],
]

function unitFor(measure: string | null | undefined): string {
  if (!measure) return ''
  for (const [pattern, unit] of UNITS) if (pattern.test(measure)) return unit
  return ''
}

function number(value: number): string {
  return value.toLocaleString('en-US', { maximumFractionDigits: 6 })
}

/**
 * One threshold value as text.
 *
 * The unit is appended to numbers only. A string threshold is a rating notch or
 * a dictionary code — `BBB-`, `SOVEREIGN` — and `BBB-%` would be nonsense.
 */
export function valueText(value: unknown, measure?: string | null): string {
  const unit = unitFor(measure)
  if (value === null || value === undefined) return ''
  if (typeof value === 'number') return `${number(value)}${unit}`
  if (typeof value === 'boolean') return value ? 'yes' : 'no'
  if (Array.isArray(value)) return value.map((v) => valueText(v, measure)).join(', ')
  if (typeof value === 'object') {
    const range = value as { min?: unknown; max?: unknown }
    if (range.min !== undefined || range.max !== undefined) {
      const lo = valueText(range.min ?? null, measure)
      const hi = valueText(range.max ?? null, measure)
      if (lo && hi) return `${lo} to ${hi}`
      return lo || hi
    }
    // A shape this release does not know. Showing the JSON is ugly and is still
    // the value; `[object Object]` is neither.
    return JSON.stringify(value)
  }
  return String(value)
}

/** What a template with no threshold is constraining instead of a number. A
 *  reader who sees one of these knows the rule is complete and knows where to
 *  look; a reader who saw `—` had to go and find out which it was. */
const WITHOUT_A_NUMBER: Record<string, string> = {
  // Only reached by a narrative rule staged before the text was kept. The
  // established phrase, and no second sentence explaining it: the row used to
  // carry both this and the `narrative — text, not a threshold` badge, which is
  // the same fact said twice in a cell the width of a threshold.
  narrative: 'narrative — text, not a threshold',
  evaluation_policy: 'How this is measured, not a limit',
  cure_obligation: 'What has to happen after a breach',
  permission_set: 'What is permitted — no number',
  restricted_list: 'A list of exclusions — no number',
}

/** A rating policy, in words. `only:S&P` is spelled out where it is used. */
const POLICIES: Record<string, string> = {
  lowest: 'split ratings: the lowest counts',
  highest: 'split ratings: the highest counts',
  middle_of_three: 'split ratings: the middle of three, or the lower of two',
  exclude: 'unrated holdings are left out of rating floors',
  fails_floor: 'an unrated holding fails a rating floor',
  fails_floor_outside_benchmark: 'an unrated holding fails a rating floor outside the benchmark, and is left out in it',
}

export interface Said {
  text: string
  /** False where the rule constrains something other than a number, so the
   *  table can render it as prose rather than as a missing figure. */
  numeric: boolean
}

export interface Limited {
  operator: string | null
  threshold: unknown
  measure?: string | null
  template?: string | null
  /** A narrative rule *is* this sentence (§7.1). Shown in place of a limit,
   *  because there is no limit — and because the alternative was showing the
   *  approver a rule key and asking them to put it into force. */
  narrative_text?: string | null
}

/**
 * The whole limit as one sentence — `at most 15%`, `between 3 and 7 years`,
 * `only AAA, AA+`, `at least BBB-`.
 *
 * The operator words are the mandate's, not the schema's: a schedule says *no
 * more than*, never *max*. Where the threshold is a range the operator word is
 * redundant and dropped, because `between between 3 and 7` is what naive
 * concatenation produced.
 */
export function says(rule: Limited): Said {
  const { operator, threshold, measure, template } = rule

  if (threshold === null || threshold === undefined) {
    // The sentence itself, where the store has it. Saying *wording, not a
    // number — read the clause* was true and useless: it named a thing the
    // approver could not reach, on a screen that was asking them to put it into
    // force. The wording is the rule, so the wording is what the cell shows.
    const wording = rule.narrative_text?.trim()
    if (wording) return { text: wording, numeric: false }

    // A permission set or restricted list still carries its list, so it only
    // reaches here when it genuinely holds nothing.
    const said = WITHOUT_A_NUMBER[template ?? '']
    if (said) return { text: said, numeric: false }
    if (operator === 'policy') return { text: 'A policy, not a number', numeric: false }
    return { text: 'No limit recorded', numeric: false }
  }

  const isRange =
    typeof threshold === 'object' &&
    threshold !== null &&
    !Array.isArray(threshold) &&
    ((threshold as { min?: unknown }).min !== undefined ||
      (threshold as { max?: unknown }).max !== undefined)

  if (isRange) {
    const range = threshold as { min?: unknown; max?: unknown }
    const lo = range.min === undefined || range.min === null ? '' : valueText(range.min, measure)
    const hi = range.max === undefined || range.max === null ? '' : valueText(range.max, measure)
    if (lo && hi) return { text: `between ${lo} and ${hi}`, numeric: true }
    if (lo) return { text: `at least ${lo}`, numeric: true }
    return { text: `at most ${hi}`, numeric: true }
  }

  // A limit against the benchmark, in the measure's own unit (decision 34).
  if (template === 'relative_limit' && typeof threshold === 'object' && threshold !== null && !Array.isArray(threshold)) {
    const t = threshold as Record<string, number | undefined>
    // Per cent of the benchmark's own figure, on a duration (decision 57).
    const proportional = t.benchmark_plus_pct !== undefined || t.benchmark_minus_pct !== undefined
    const years = /duration/.test(measure ?? '')
    const offset = (n: number) =>
      proportional
        ? `${number(n)}%`
        : years
          ? `${number(n)} year${n === 1 ? '' : 's'}`
          : `${number(n)}${unitFor(measure) === '%' ? ' pts' : ''}`
    const plus = proportional ? t.benchmark_plus_pct : t.benchmark_plus
    const minus = proportional ? t.benchmark_minus_pct : t.benchmark_minus
    if (plus !== undefined && minus !== undefined) {
      return {
        text: plus === minus
          ? `within ${offset(plus)} of the benchmark`
          : `between the benchmark − ${offset(minus)} and + ${offset(plus)}`,
        numeric: true,
      }
    }
    if (plus !== undefined) return { text: `at most the benchmark + ${offset(plus)}`, numeric: true }
    if (minus !== undefined) return { text: `at least the benchmark − ${offset(minus)}`, numeric: true }
  }

  // A notch, and which holdings it is read against: every one of them, or the
  // fund's average. The same `at least BB-` is two different limits.
  if (template === 'rating_floor' && typeof threshold === 'string') {
    return { text: `every holding rated at least ${threshold}`, numeric: true }
  }
  if (template === 'average_rating_floor' && typeof threshold === 'string') {
    return { text: `an average rating of at least ${threshold}`, numeric: true }
  }
  if (operator === 'policy' && typeof threshold === 'string') {
    // A sell-by period, as an ISO 8601 duration: `P6M` is six months (decision 36).
    const period = /^P(\d+)([DWMY])$/.exec(threshold)
    if (period) {
      const n = Number(period[1])
      const unit = ({ D: 'day', W: 'week', M: 'month', Y: 'year' } as const)[period[2] as 'D' | 'W' | 'M' | 'Y']
      return {
        text: `a holding that falls below its rating floor is sold within ${n} ${unit}${n === 1 ? '' : 's'}`,
        numeric: false,
      }
    }
    const only = threshold.startsWith('only:') ? `split ratings: only ${threshold.slice(5)} counts` : null
    const policy = POLICIES[threshold] ?? only
    if (policy) return { text: policy, numeric: false }
  }

  const value = valueText(threshold, measure)

  if (Array.isArray(threshold)) {
    return {
      text: operator === 'not_in' ? `none of ${value}` : `only ${value}`,
      numeric: true,
    }
  }

  switch (operator) {
    case 'max':
      return { text: `at most ${value}`, numeric: true }
    case 'min':
      return { text: `at least ${value}`, numeric: true }
    case 'equals':
      return { text: `exactly ${value}`, numeric: true }
    case 'in':
      return { text: `only ${value}`, numeric: true }
    case 'not_in':
      return { text: `none of ${value}`, numeric: true }
    default:
      // An operator the display does not know is still shown with its value
      // rather than swallowed — the store is the authority on what is valid.
      return { text: `${operator ?? ''} ${value}`.trim(), numeric: true }
  }
}
